/**
 * Shared agent picker preset for foundation {@link CoordioDropdown}.
 *
 * Mirrors CoordioCore's `ORGANIZATION_DROPDOWN` shape: one spreadable preset + a
 * row mapper so every agent select (orchestrator, session create, …) renders the
 * same searchable detail row (name + slug) instead of a hand-rolled Input list.
 */
import { Bot } from 'lucide-react';
import type { CoordioDropdownOption, CoordioDropdownOptionTemplate, CoordioDropdownTriggerTemplate } from '@coordio/ui';

import type { AgentDefinitionDto } from '../../types/agent';

/** Spread onto `CoordioDropdown` — same idea as CoordioCore `ORGANIZATION_DROPDOWN`. */
export const AGENT_DROPDOWN = {
  searchable: true as const,
  clearable: true as const,
  optionTemplate: { preset: 'detail' } satisfies CoordioDropdownOptionTemplate,
  triggerTemplate: { icon: <Bot size={15} aria-hidden /> } satisfies CoordioDropdownTriggerTemplate,
  placeholder: 'Search agents…',
  searchPlaceholder: 'Search agents…',
  emptyMessage: 'No matching agents found',
};

/** Maps an agent DTO to a detail-preset dropdown row (title = name, subtitle = slug). */
export function agentToDropdownOption(
  agent: AgentDefinitionDto,
): CoordioDropdownOption<AgentDefinitionDto> {
  return {
    value: agent.id,
    label: agent.name,
    description: agent.slug,
    badge: agent.category ?? null,
    tags: agent.isActive ? undefined : ['Inactive'],
    keywords: [agent.slug, agent.description ?? '', agent.category ?? ''].filter(Boolean),
    raw: agent,
  };
}
